/**
 * @file src/ui/localFonts.js
 * @description Recognises a font-host stylesheet URL so the injectors drop it instead of fetching it.
 *
 * The theme catalog used to hand every font out as an `importUrl` — a stylesheet on a third-party
 * font host — and the consumer injected a `<link>` for it. That request is pure cost: it leaks every
 * visitor's IP to that host before they interact with anything, and this player is embedded on sites
 * that promise no third-party request fires on load.
 *
 * The catalog now sets `importUrl: null` for every family and the design library ships the woff2 for
 * all eighteen, so no family a URL can name is one we cannot render locally. Filtering at the
 * INJECTION point — not at the catalog — remains deliberate: an orbiter's design is persisted with
 * whatever `fontImportUrl` was current when it was saved, so old records carry remote URLs no catalog
 * edit can reach. Those records are the only thing that still reaches here.
 *
 * This deliberately does NOT enumerate family names. It used to, and the list went stale the moment
 * this app stopped shipping its own three families and took all eighteen from the library — every
 * other family kept hotlinking. A rule with nothing to keep in sync cannot drift that way again.
 */

/**
 * Family names a font-host stylesheet URL asks for, lowercased.
 * Handles both the `css2` shape (one `family` param each) and the older `css` shape
 * (families joined by `|`); either way the name ends at the `:` before its weight spec.
 * @param {string} url
 * @returns {string[]}
 */
function requestedFamilies(url) {
  let params;
  try {
    params = new URL(url, 'https://example.invalid').searchParams;
  } catch (_) {
    return [];
  }
  const families = [];
  for (const value of params.getAll('family')) {
    for (const entry of value.split('|')) {
      const name = entry.split(':')[0].trim().toLowerCase();
      if (name) families.push(name);
    }
  }
  return families;
}

/**
 * True when the URL is a font-host stylesheet naming families we ship, so the request can be dropped.
 *
 * Every family the catalog can name is bundled, so naming any family at all is enough. A URL we
 * cannot read a family out of is left alone rather than dropped — it cannot have come from the
 * catalog, and dropping something unrecognised would lose a face for no privacy gain.
 * @param {string|null|undefined} url - Font stylesheet URL from a catalog/design `importUrl`.
 * @returns {boolean}
 */
export function isBundledFontImport(url) {
  if (!url) return false;
  return requestedFamilies(url).length > 0;
}
